import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { MiDetailFields, MiModal } from "@miplatform/components";
import { BUTTON_VARIANTS, INPUT_TYPES } from "@miplatform/constants";
import { ISeriesResponse } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAddSeriesModalViewModel } from "./useAddSeriesModalViewModel";

const schema = z.object({
  name: z.string().min(1, "Series Name is required"),
  accountId: z.string().uuid(),
  seriesGroupId: z.string().uuid().nullable(),
});

interface SeriesFormValues {
  id?: string;
  modifiedOn?: string;
  name: string;
  accountId: string;
  seriesGroupId: string | null;
}

interface AddSeriesModalProps {
  open: boolean;
  closeModal: () => void;
  selectedSeries: ISeriesResponse | null;
  setSelectedSeries: (series: ISeriesResponse | null) => void;
  setRefresh: (updater: (prev: boolean) => boolean) => void;
  accountId: string;
  seriesGroups: { id: string; name: string }[];
}

export function AddSeriesModal({
  open,
  closeModal,
  selectedSeries,
  setSelectedSeries,
  setRefresh,
  accountId,
  seriesGroups,
}: AddSeriesModalProps) {
  const {
    formState: { isValid, isDirty },
    control,
    getValues,
    reset,
  } = useForm<SeriesFormValues>({
    mode: "onChange",
    defaultValues: {
      name: "",
      accountId,
      seriesGroupId: null,
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (selectedSeries?.id) {
      reset({
        id: selectedSeries.id,
        modifiedOn: selectedSeries.modifiedOn,
        name: selectedSeries.name,
        accountId: selectedSeries.accountId,
        seriesGroupId: selectedSeries.seriesGroupId ?? null,
      });
    } else {
      reset({ name: "", accountId, seriesGroupId: null });
    }
  }, [selectedSeries, accountId]);

  const handleCloseModal = () => {
    setSelectedSeries(null);
    closeModal();
    reset();
  };

  const { isLoading, handleCreateSeries, handleUpdateSeries } =
    useAddSeriesModalViewModel({
      setRefresh,
      handleCloseModal,
    });

  const seriesGroupOptions = useMemo(
    () => [
      { label: "None", value: null },
      ...seriesGroups.map((group) => ({ label: group.name, value: group.id })),
    ],
    [seriesGroups]
  );

  const seriesFields = [
    {
      label: "Series Name",
      name: "name",
      formControl: control,
      required: true,
      inputProps: { maxLength: 128 },
      width: "100%",
    },
    {
      label: "Series Group",
      name: "seriesGroupId",
      formControl: control,
      fieldType: INPUT_TYPES.SELECT,
      options: seriesGroupOptions,
      width: "100%",
    },
  ];

  const handleSave = () => {
    handleCreateSeries(getValues());
  };

  const handleUpdate = () => {
    handleUpdateSeries(getValues());
  };

  return (
    <MiModal
      open={open}
      closeModal={handleCloseModal}
      loading={isLoading}
      title={`${selectedSeries?.id ? "Edit" : "Add"} Series`}
      description={`${
        selectedSeries?.id ? "Edit" : "Enter"
      } series details below.`}
      actions={[
        {
          name: "Cancel",
          onClick: handleCloseModal,
        },
        {
          name: selectedSeries?.id ? "Update Series" : "Save Series",
          variant: BUTTON_VARIANTS.CONTAINED,
          onClick: selectedSeries?.id ? handleUpdate : handleSave,
          disabled: !isValid || !isDirty,
        },
      ]}
    >
      <MiDetailFields detailFields={seriesFields} />
    </MiModal>
  );
}
